import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { rcmKeys } from '@/api/queryKeys';
import { pmsAccessApi } from '../api/pmsAccessApi';

/**
 * Settings › PMS access.
 *
 * Refetched on a timer, unlike the rest of Settings: lag is the number this
 * page exists to show, and a stale one is worse than none — somebody reading
 * "4 minutes behind" from twenty minutes ago will make the wrong call about
 * whether the sync is stuck.
 *
 * Takes a nullable id because the hook has to be called before the component
 * knows there is a practice; `enabled` keeps it from firing, and the throw is
 * how that is said in a way the types agree with.
 */
export function usePmsAccess(practiceId: string | null) {
  return useQuery({
    queryKey: rcmKeys.pmsAccess.status(practiceId ?? ''),
    queryFn: () => {
      if (practiceId === null) throw new Error('no practice to ask about');
      return pmsAccessApi.status(practiceId);
    },
    enabled: practiceId !== null,
    refetchInterval: 60 * 1000,
    staleTime: 30 * 1000,
  });
}

export function useStopPmsAccess(practiceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) => pmsAccessApi.stop(practiceId, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: rcmKeys.pmsAccess.status(practiceId),
      });
    },
  });
}

export function useStorePmsCredential(practiceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { kind: string; label: string; secret: string; username?: string }) =>
      pmsAccessApi.storeCredential(practiceId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: rcmKeys.pmsAccess.status(practiceId),
      });
    },
  });
}
